import { supabase } from "@supabaseClient/supabaseClient";

/**
 * Purchase Order status type
 */
export type PurchaseOrderStatus =
  | "draft"
  | "pending"
  | "ordered"
  | "partial"
  | "received"
  | "cancelled";

/**
 * Purchase Order Item status type
 */
export type PurchaseOrderItemStatus =
  | "pending"
  | "ordered"
  | "partial"
  | "received"
  | "cancelled";

/**
 * Purchase Order interface matching the database schema
 */
export interface IPurchaseOrder {
  id: string;
  po_number: string;
  supplier_id: string;
  organization_id: string;
  status: PurchaseOrderStatus;
  order_date?: string | null;
  expected_delivery_date?: string | null;
  received_date?: string | null;
  notes?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  total_amount?: number | null;
  currency?: string | null;
}

/**
 * Purchase Order Item interface matching the database schema
 */
export interface IPurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  product_id: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_price?: number | null;
  organization_id: string;
  status: PurchaseOrderItemStatus;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  product?: {
    id: string;
    name: string;
    sku?: string | null;
  } | null;
}

/**
 * Purchase Order with items
 */
export interface IPurchaseOrderWithItems extends IPurchaseOrder {
  purchase_order_items: IPurchaseOrderItem[];
}

/**
 * Purchase Order with supplier information
 */
export interface IPurchaseOrderWithSupplier extends IPurchaseOrder {
  supplier?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
}

/**
 * Purchase Order with items and supplier
 */
export interface IPurchaseOrderWithItemsAndSupplier
  extends IPurchaseOrderWithItems {
  supplier?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
}

/**
 * Purchase Order Fulfillment - links PO items to customer orders/backorders
 */
export interface IPurchaseOrderFulfillment {
  id: string;
  purchase_order_id: string;
  purchase_order_item_id: string;
  backorder_id?: string | null;
  order_id?: string | null;
  order_item_id?: string | null;
  quantity_allocated: number;
  organization_id: string;
  created_at: string;
}

/**
 * Purchase Order Receipt
 */
export interface IPurchaseOrderReceipt {
  id: string;
  purchase_order_id: string;
  received_by?: string | null;
  received_by_user?: {
    id: string;
    name?: string | null;
  } | null;
  received_at: string;
  notes?: string | null;
  organization_id: string;
  created_at: string;
}

/**
 * Purchase Order Receipt Item
 */
export interface IPurchaseOrderReceiptItem {
  id: string;
  receipt_id: string;
  purchase_order_item_id: string;
  product_id: string;
  quantity_received: number;
  location_id?: string | null;
  organization_id: string;
  created_at: string;
}

/**
 * Purchase Order Planned Allocation
 */
export interface IPurchaseOrderPlannedAllocation {
  id: string;
  purchase_order_id: string;
  purchase_order_item_id: string;
  order_id: string;
  order_item_id: string;
  product_id: string;
  quantity_planned: number;
  organization_id: string;
  created_at: string;
  updated_at?: string;
  order?: {
    id: string;
    order_number: string;
    customer_name?: string | null;
  } | null;
  order_item?: {
    id: string;
    quantity_requested?: number | null;
  } | null;
  product?: {
    id: string;
    name?: string | null;
    sku?: string | null;
  } | null;
}

export class PurchaseOrdersClient {
  /**
   * Get all purchase orders for an organization
   */
  static async getPurchaseOrdersByOrganizationId(
    organization_id: string,
    includeSupplier: boolean = false
  ): Promise<
    IPurchaseOrderWithItems[] | IPurchaseOrderWithItemsAndSupplier[]
  > {
    const selectQuery = includeSupplier
      ? `*, purchase_order_items (*), supplier:suppliers!supplier_id (id, name, email, phone)`
      : `*, purchase_order_items (*)`;

    const { data, error } = await supabase
      .from("purchase_orders")
      .select(selectQuery)
      .eq("organization_id", organization_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }
    return (data || []) as unknown as
      | IPurchaseOrderWithItems[]
      | IPurchaseOrderWithItemsAndSupplier[];
  }

  /**
   * Get a purchase order by ID
   */
  static async getPurchaseOrderById(
    purchase_order_id: string,
    includeSupplier: boolean = false
  ): Promise<IPurchaseOrderWithItems | IPurchaseOrderWithItemsAndSupplier> {
    const selectQuery = includeSupplier
      ? `*, purchase_order_items (*), supplier:suppliers!supplier_id (id, name, email, phone)`
      : `*, purchase_order_items (*)`;

    const { data, error } = await supabase
      .from("purchase_orders")
      .select(selectQuery)
      .eq("id", purchase_order_id)
      .single();

    if (error) {
      throw error;
    }
    return data as unknown as
      | IPurchaseOrderWithItems
      | IPurchaseOrderWithItemsAndSupplier;
  }

  /**
   * Create a new purchase order
   * If supplier_id is not provided, will find or create supplier
   */
  static async createPurchaseOrder(
    purchaseOrder: Omit<
      IPurchaseOrder,
      "id" | "po_number" | "created_at" | "updated_at"
    >
  ): Promise<IPurchaseOrder> {
    // Ensure supplier exists
    if (!purchaseOrder.supplier_id) {
      throw new Error("supplier_id is required to create a purchase order");
    }

    // Ensure organization_id exists
    if (!purchaseOrder.organization_id) {
      throw new Error("organization_id is required to create a purchase order");
    }

    // Generate PO number using database function (organization-scoped)
    const poNumber = await this.generatePONumber(purchaseOrder.organization_id);

    const { data, error } = await supabase
      .from("purchase_orders")
      .insert([
        {
          ...purchaseOrder,
          po_number: poNumber,
        },
      ])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Update a purchase order
   */
  static async updatePurchaseOrder(
    purchaseOrder: Omit<IPurchaseOrder, "created_at" | "updated_at">
  ): Promise<IPurchaseOrder> {
    const { data, error } = await supabase
      .from("purchase_orders")
      .update(purchaseOrder)
      .eq("id", purchaseOrder.id)
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Delete a purchase order
   */
  static async deletePurchaseOrder(id: string): Promise<void> {
    const { error } = await supabase
      .from("purchase_orders")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }
  }

  /**
   * Create a purchase order item
   */
  static async createPurchaseOrderItem(
    purchaseOrderItem: Omit<IPurchaseOrderItem, "id" | "created_at" | "updated_at">
  ): Promise<IPurchaseOrderItem> {
    const { data, error } = await supabase
      .from("purchase_order_items")
      .insert([purchaseOrderItem])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Update a purchase order item
   */
  static async updatePurchaseOrderItem(
    purchaseOrderItem: Omit<IPurchaseOrderItem, "created_at" | "updated_at">
  ): Promise<IPurchaseOrderItem> {
    const { data, error } = await supabase
      .from("purchase_order_items")
      .update(purchaseOrderItem)
      .eq("id", purchaseOrderItem.id)
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Delete a purchase order item
   */
  static async deletePurchaseOrderItem(id: string): Promise<void> {
    const { error } = await supabase
      .from("purchase_order_items")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }
  }

  /**
   * Generate a unique PO number using database function
   * Format: YYYY-MM-XXX (e.g., 2024-01-001)
   * Organization-scoped to ensure uniqueness per organization
   */
  private static async generatePONumber(
    organizationId: string
  ): Promise<string> {
    const { data, error } = await supabase.rpc("generate_po_number", {
      org_id: organizationId,
    });

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Update purchase order status
   */
  static async updatePurchaseOrderStatus(
    purchaseOrderId: string,
    status: PurchaseOrderStatus,
    organizationId: string
  ): Promise<IPurchaseOrder> {
    const updateData: Partial<IPurchaseOrder> = {
      status,
      updated_at: new Date().toISOString(),
    };

    // Set received_date if status is 'received'
    if (status === "received") {
      updateData.received_date = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from("purchase_orders")
      .update(updateData)
      .eq("id", purchaseOrderId)
      .eq("organization_id", organizationId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update purchase order status: ${error.message}`);
    }

    return data;
  }

  /**
   * Get purchase orders by status
   */
  static async getPurchaseOrdersByStatus(
    status: PurchaseOrderStatus,
    organizationId: string
  ): Promise<IPurchaseOrder[]> {
    const { data, error } = await supabase
      .from("purchase_orders")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch purchase orders by status: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Link a purchase order to a supplier
   */
  static async linkPurchaseOrderToSupplier(
    purchaseOrderId: string,
    supplierId: string
  ): Promise<IPurchaseOrder> {
    const { data, error } = await supabase
      .from("purchase_orders")
      .update({ supplier_id: supplierId })
      .eq("id", purchaseOrderId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to link purchase order to supplier: ${error.message}`);
    }

    return data;
  }

  /**
   * Create a purchase order fulfillment link
   * Links a PO item to customer orders/backorders
   */
  static async createPurchaseOrderFulfillment(
    fulfillment: Omit<IPurchaseOrderFulfillment, "id" | "created_at">
  ): Promise<IPurchaseOrderFulfillment> {
    const { data, error } = await supabase
      .from("purchase_order_fulfillments")
      .insert([fulfillment])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Get purchase order fulfillments for a purchase order
   */
  static async getPurchaseOrderFulfillments(
    purchaseOrderId: string
  ): Promise<IPurchaseOrderFulfillment[]> {
    const { data, error } = await supabase
      .from("purchase_order_fulfillments")
      .select("*")
      .eq("purchase_order_id", purchaseOrderId);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get purchase order fulfillments for a backorder
   */
  static async getPurchaseOrderFulfillmentsByBackorder(
    backorderId: string
  ): Promise<IPurchaseOrderFulfillment[]> {
    const { data, error } = await supabase
      .from("purchase_order_fulfillments")
      .select("*")
      .eq("backorder_id", backorderId);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get purchase order fulfillments for a customer order
   */
  static async getPurchaseOrderFulfillmentsByOrder(
    orderId: string
  ): Promise<IPurchaseOrderFulfillment[]> {
    const { data, error } = await supabase
      .from("purchase_order_fulfillments")
      .select("*")
      .eq("order_id", orderId);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Create a purchase order receipt
   */
  static async createPurchaseOrderReceipt(
    receipt: Omit<IPurchaseOrderReceipt, "id" | "created_at">
  ): Promise<IPurchaseOrderReceipt> {
    const { data, error } = await supabase
      .from("purchase_order_receipts")
      .insert([receipt])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Create purchase order receipt items (bulk)
   */
  static async createPurchaseOrderReceiptItems(
    receiptItems: Array<Omit<IPurchaseOrderReceiptItem, "id" | "created_at">>
  ): Promise<IPurchaseOrderReceiptItem[]> {
    const { data, error } = await supabase
      .from("purchase_order_receipt_items")
      .insert(receiptItems)
      .select();

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get purchase order receipts by purchase order
   */
  static async getPurchaseOrderReceiptsByPurchaseOrderId(
    purchaseOrderId: string
  ): Promise<IPurchaseOrderReceipt[]> {
    const { data, error } = await supabase
      .from("purchase_order_receipts")
      .select("*, received_by_user:users!received_by (id, name)")
      .eq("purchase_order_id", purchaseOrderId)
      .order("received_at", { ascending: false });

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get receipt items by receipt
   */
  static async getPurchaseOrderReceiptItemsByReceiptId(
    receiptId: string
  ): Promise<IPurchaseOrderReceiptItem[]> {
    const { data, error } = await supabase
      .from("purchase_order_receipt_items")
      .select("*")
      .eq("receipt_id", receiptId);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get planned allocations for a purchase order
   */
  static async getPlannedAllocationsByPurchaseOrderId(
    purchaseOrderId: string
  ): Promise<IPurchaseOrderPlannedAllocation[]> {
    const { data, error } = await supabase
      .from("purchase_order_planned_allocations")
      .select(
        `
        *,
        order:orders (id, order_number, customer_name),
        order_item:order_items (id, quantity_requested),
        product:products (id, name, sku)
      `
      )
      .eq("purchase_order_id", purchaseOrderId)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }
    return (data || []) as IPurchaseOrderPlannedAllocation[];
  }

  /**
   * Create a planned allocation for a purchase order
   */
  static async createPlannedAllocation(
    allocation: Omit<
      IPurchaseOrderPlannedAllocation,
      "id" | "created_at" | "updated_at" | "order" | "order_item" | "product"
    >
  ): Promise<IPurchaseOrderPlannedAllocation> {
    const { data, error } = await supabase
      .from("purchase_order_planned_allocations")
      .insert([allocation])
      .select(
        `
        *,
        order:orders (id, order_number, customer_name),
        order_item:order_items (id, quantity_requested),
        product:products (id, name, sku)
      `
      )
      .single();

    if (error) {
      throw error;
    }
    return data as IPurchaseOrderPlannedAllocation;
  }

  /**
   * Update a planned allocation
   */
  static async updatePlannedAllocation(
    allocation: Pick<IPurchaseOrderPlannedAllocation, "id" | "quantity_planned">
  ): Promise<IPurchaseOrderPlannedAllocation> {
    const { data, error } = await supabase
      .from("purchase_order_planned_allocations")
      .update({ quantity_planned: allocation.quantity_planned })
      .eq("id", allocation.id)
      .select(
        `
        *,
        order:orders (id, order_number, customer_name),
        order_item:order_items (id, quantity_requested),
        product:products (id, name, sku)
      `
      )
      .single();

    if (error) {
      throw error;
    }
    return data as IPurchaseOrderPlannedAllocation;
  }

  /**
   * Delete a planned allocation
   */
  static async deletePlannedAllocation(id: string): Promise<void> {
    const { error } = await supabase
      .from("purchase_order_planned_allocations")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }
  }

  /**
   * Calculate total amount for a purchase order based on its items
   */
  static async calculatePurchaseOrderTotal(
    purchaseOrderId: string
  ): Promise<number> {
    const { data, error } = await supabase
      .from("purchase_order_items")
      .select("quantity_ordered, unit_price")
      .eq("purchase_order_id", purchaseOrderId);

    if (error) {
      throw error;
    }

    const total = (data || []).reduce((sum, item) => {
      const itemTotal =
        (item.quantity_ordered || 0) *
        (item.unit_price ? Number(item.unit_price) : 0);
      return sum + itemTotal;
    }, 0);

    return total;
  }

  /**
   * Update purchase order total amount
   */
  static async updatePurchaseOrderTotal(
    purchaseOrderId: string
  ): Promise<IPurchaseOrder> {
    const total = await this.calculatePurchaseOrderTotal(purchaseOrderId);

    const { data, error } = await supabase
      .from("purchase_orders")
      .update({ total_amount: total })
      .eq("id", purchaseOrderId)
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }
}
